import * as actionTypes from "../actions/actionTypes";
import { updateObject } from "../utility";

const initialState = {
  // Aggregated orders list (replaces old individual orders list)
  orders: [],
  loading: false,
  error: false,
  numberOfItems: 0,
  pageCount: 0,
  // Legacy filter fields kept for Layout.js mapStateToProps compatibility
  sortDateOrder: 1,
  statusOrder: 0,
  perPageSelect: 10,
  selectedPage: 0,
  statusID: 0,

  // Legacy individual order detail (kept for OrderDetails.js backward compatibility)
  oneOrderLoading: false,
  orderDetails: null,
  done: false,
  confirmLoading: false,
  rejectProductLoading: false,

  // Aggregated order detail
  aggOrderLoading: false,
  aggOrderDetails: null,
  aggConfirmLoading: false,
  itemUpdateLoading: false,
};

// ==============================
// AGGREGATED ORDERS LIST
// ==============================

const getOrdersStart = (state) =>
  updateObject(state, { loading: true, error: false });

const getOrdersSuccess = (state, action) => {
  // Response is a flat array of AggregatedOrderWithDetails
  const orders = Array.isArray(action.data) ? action.data : [];
  return updateObject(state, {
    loading: false,
    orders,
    numberOfItems: orders.length,
    pageCount: 0,
  });
};

const getOrdersFail = (state) =>
  updateObject(state, { loading: false, error: true });

// ==============================
// AGGREGATED ORDER DETAIL
// ==============================

const getOneAggOrderStart = (state) =>
  updateObject(state, { aggOrderLoading: true, aggOrderDetails: null });

const getOneAggOrderSuccess = (state, action) =>
  updateObject(state, { aggOrderLoading: false, aggOrderDetails: action.data });

const getOneAggOrderFail = (state) =>
  updateObject(state, { aggOrderLoading: false, aggOrderDetails: null });

// ==============================
// AGGREGATED ORDER CONFIRM
// ==============================

const confirmAggOrderStart = (state) =>
  updateObject(state, { aggConfirmLoading: true });

const confirmAggOrderSuccess = (state) =>
  updateObject(state, { aggConfirmLoading: false });

const confirmAggOrderFail = (state) =>
  updateObject(state, { aggConfirmLoading: false });

// ==============================
// LEGACY: INDIVIDUAL ORDER DETAIL
// ==============================

const getOneOrderStart = (state) =>
  updateObject(state, { oneOrderLoading: true, done: false });

const getOneOrderSuccess = (state, action) =>
  updateObject(state, { oneOrderLoading: false, orderDetails: action.data });

const getOneOrderFail = (state) =>
  updateObject(state, { oneOrderLoading: false, orderDetails: null });

const confirmOrderStart = (state) =>
  updateObject(state, { done: false, confirmLoading: true });

const confirmOrderSuccess = (state) =>
  updateObject(state, { done: true, confirmLoading: false });

const confirmOrderFail = (state) =>
  updateObject(state, { done: true, confirmLoading: false });

const rejectProductStart = (state) =>
  updateObject(state, { rejectProductLoading: true });

const rejectProductSuccess = (state) =>
  updateObject(state, { rejectProductLoading: false });

const rejectProductFail = (state) =>
  updateObject(state, { rejectProductLoading: false });

// ==============================
// AGGREGATED ORDER ITEM UPDATE
// ==============================

const updateAggItemStart = (state) =>
  updateObject(state, { itemUpdateLoading: true });

const updateAggItemSuccess = (state) =>
  updateObject(state, { itemUpdateLoading: false });

const updateAggItemFail = (state) =>
  updateObject(state, { itemUpdateLoading: false });

// ==============================
// PAGINATION / FILTER HELPERS (Layout.js compatibility)
// ==============================

const updatePerPageSelectStart = (state, action) => {
  switch (action.name) {
    case "sortDateOrder":
      return updateObject(state, { [action.name]: action.value, selectedPage: 0 });
    case "perPageSelect":
      return updateObject(state, {
        [action.name]: action.value,
        pageCount: Math.ceil(state.numberOfItems / action.value),
        selectedPage: 0,
      });
    case "statusID":
      return updateObject(state, { [action.name]: action.value, selectedPage: 0 });
    default:
      return state;
  }
};

const updatePageSelectStart = (state, action) =>
  updateObject(state, { selectedPage: action.value });

// ==============================
// ROOT REDUCER
// ==============================

const reducer = (state = initialState, action) => {
  switch (action.type) {
    // Aggregated orders list
    case actionTypes.GET_ORDERS_START:
      return getOrdersStart(state, action);
    case actionTypes.GET_ORDERS_SUCCESS:
      return getOrdersSuccess(state, action);
    case actionTypes.GET_ORDERS_FAIL:
      return getOrdersFail(state, action);

    // Aggregated order detail
    case actionTypes.GET_ONE_AGG_ORDER_START:
      return getOneAggOrderStart(state, action);
    case actionTypes.GET_ONE_AGG_ORDER_SUCCESS:
      return getOneAggOrderSuccess(state, action);
    case actionTypes.GET_ONE_AGG_ORDER_FAIL:
      return getOneAggOrderFail(state, action);

    // Aggregated order confirm
    case actionTypes.CONFIRM_AGG_ORDER_START:
      return confirmAggOrderStart(state, action);
    case actionTypes.CONFIRM_AGG_ORDER_SUCCESS:
      return confirmAggOrderSuccess(state, action);
    case actionTypes.CONFIRM_AGG_ORDER_FAIL:
      return confirmAggOrderFail(state, action);

    // Legacy individual order detail
    case actionTypes.GET_ONE_ORDER_START:
      return getOneOrderStart(state, action);
    case actionTypes.GET_ONE_ORDER_SUCCESS:
      return getOneOrderSuccess(state, action);
    case actionTypes.GET_ONE_ORDER_FAIL:
      return getOneOrderFail(state, action);
    case actionTypes.CONFIRM_ORDER_START:
      return confirmOrderStart(state, action);
    case actionTypes.CONFIRM_ORDER_SUCCESS:
      return confirmOrderSuccess(state, action);
    case actionTypes.CONFIRM_ORDER_FAIL:
      return confirmOrderFail(state, action);
    case actionTypes.REJECT_PRODUCT_START:
      return rejectProductStart(state, action);
    case actionTypes.REJECT_PRODUCT_SUCCESS:
      return rejectProductSuccess(state, action);
    case actionTypes.REJECT_PRODUCT_FAIL:
      return rejectProductFail(state, action);

    // Aggregated order item update
    case actionTypes.UPDATE_AGG_ORDER_ITEM_START:
      return updateAggItemStart(state, action);
    case actionTypes.UPDATE_AGG_ORDER_ITEM_SUCCESS:
      return updateAggItemSuccess(state, action);
    case actionTypes.UPDATE_AGG_ORDER_ITEM_FAIL:
      return updateAggItemFail(state, action);

    // Pagination/filter helpers
    case actionTypes.UPDATE_SELECT_PER_PAGE:
      return updatePerPageSelectStart(state, action);
    case actionTypes.UPDATE_SELECT_PAGE:
      return updatePageSelectStart(state, action);

    default:
      return state;
  }
};

export default reducer;
